/**
 * ChampionshipsPage — Steward Admin (Multi-Championship)
 *
 * Lists all championships with status badges, and routes into a detail view.
 * The wizard, detail view, modals and card were split into
 * src/components/championships/ — this file is now just the list and its
 * navigation state.
 */
import React, { useEffect, useState } from 'react';
import { autoStatus, onChampionships } from '../services/tripleCrown';
import ChampionshipCard from '../components/championships/ChampionshipCard';
import ChampionshipDetail from '../components/championships/ChampionshipDetail';
import ChampionshipWizard from '../components/championships/ChampionshipWizard';

/* ══════════════════════════════════════
   MAIN PAGE — CHAMPIONSHIPS LIST
   ══════════════════════════════════════ */
export default function ChampionshipsPage() {
  const [championships, setChampionships] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('list'); // 'list' | 'wizard' | 'detail'
  const [selectedChamp, setSelectedChamp] = useState(null);

  useEffect(() => {
    return onChampionships(champs => {
      setChampionships(champs);
      setLoading(false);
    });
  }, []);

  const openDetail = (champ) => {
    setSelectedChamp(champ);
    setView('detail');
  };

  if (view === 'wizard') {
    return (
      <div>
        <div className="page-header">
          <div>
            <h1 className="page-title">Create Championship</h1>
            <p className="page-subtitle">Set up a new championship with rounds and settings</p>
          </div>
        </div>
        <ChampionshipWizard onDone={() => setView('list')} />
      </div>
    );
  }

  if (view === 'detail' && selectedChamp) {
    // Find the latest version from the live list
    const liveChamp = championships.find(c => c.id === selectedChamp.id) || selectedChamp;
    return (
      <div>
        <div className="page-header">
          <div>
            <h1 className="page-title">{liveChamp.title}</h1>
            <p className="page-subtitle">Manage rounds, events, and settings</p>
          </div>
        </div>
        <ChampionshipDetail championship={liveChamp} onBack={() => setView('list')} />
      </div>
    );
  }

  // Group championships by status
  const active = championships.filter(c => autoStatus(c.startDate, c.endDate) === 'active');
  const upcoming = championships.filter(c => autoStatus(c.startDate, c.endDate) === 'upcoming');
  const completed = championships.filter(c => autoStatus(c.startDate, c.endDate) === 'completed');

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Championships</h1>
          <p className="page-subtitle">
            {championships.length} championship{championships.length !== 1 ? 's' : ''}
            {active.length > 0 && ` · ${active.length} active`}
          </p>
        </div>
        <button className="btn btn-primary" onClick={() => setView('wizard')}>
          + New Championship
        </button>
      </div>

      {loading ? (
        <div className="card" style={{ padding: 24 }}>
          <p style={{ color: 'var(--text-muted)', margin: 0 }}>Loading championships...</p>
        </div>
      ) : championships.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: '48px 20px' }}>
          <p style={{ color: 'var(--text-muted)', fontSize: 15, marginBottom: 18 }}>
            No championships yet. Create your first championship to get started.
          </p>
          <button className="btn btn-primary" onClick={() => setView('wizard')}>
            + Create Championship
          </button>
        </div>
      ) : (
        <>
          {/* Active championships */}
          {active.length > 0 && (
            <div style={{ marginBottom: 24 }}>
              <h3 style={{ fontSize: 14, color: 'var(--text-muted)', marginBottom: 10, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Active
              </h3>
              <div style={{ display: 'grid', gap: 12 }}>
                {active.map(c => (
                  <ChampionshipCard key={c.id} championship={c} onClick={() => openDetail(c)} />
                ))}
              </div>
            </div>
          )}

          {/* Upcoming */}
          {upcoming.length > 0 && (
            <div style={{ marginBottom: 24 }}>
              <h3 style={{ fontSize: 14, color: 'var(--text-muted)', marginBottom: 10, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Upcoming
              </h3>
              <div style={{ display: 'grid', gap: 12 }}>
                {upcoming.map(c => (
                  <ChampionshipCard key={c.id} championship={c} onClick={() => openDetail(c)} />
                ))}
              </div>
            </div>
          )}

          {/* Completed */}
          {completed.length > 0 && (
            <div style={{ marginBottom: 24 }}>
              <h3 style={{ fontSize: 14, color: 'var(--text-muted)', marginBottom: 10, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Completed
              </h3>
              <div style={{ display: 'grid', gap: 12 }}>
                {completed.map(c => (
                  <ChampionshipCard key={c.id} championship={c} onClick={() => openDetail(c)} />
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
